import { useCallback } from 'react'

import { useNavigate } from 'react-router-dom'

import { SingleTrackAudioPlayer } from 'components/SingleTrackAudioPlayer'
import type {
  ErrorStates,
  PlayProperties,
} from 'hooks/useSingleTrackAudioPlayer/types'
import type { EventTrack } from 'types/player'
import { debugError, debugLog } from 'utils/debug'
import { PageEventType, TrackEventType } from 'utils/rudderstack'
import { useVisitorEventTracking } from 'utils/rudderstack/useVisitorEventTracking'

export const Player = () => {
  const navigate = useNavigate()

  const track = useVisitorEventTracking({
    pageName: PageEventType.LISTENING,
  })

  const onActiveFinishedAtInit = useCallback(
    (trackId: string) => {
      debugLog(
        'cb: onActiveFinishedAtInit: Active event track is already over when we hit play',
        trackId,
      )
      // Callback event
      track({
        event: TrackEventType.ACTIVE_FINISHED_AT_INIT,
        custom: { content_id: trackId },
      })
    },
    [track],
  )

  const onActiveFinished = useCallback(
    (trackId: string) => {
      debugLog(
        'cb: onActiveFinished: Active event track playback ended.',
        trackId,
      )
      // Callback event
      track({
        event: TrackEventType.ACTIVE_FINISHED,
        custom: { content_id: trackId },
      })
    },
    [track],
  )
  const onPlay = useCallback(
    ({
      trackId,
      trackCurrentProgress,
      trackDuration,
      isPlaying,
    }: PlayProperties) => {
      debugLog('cb: onPlay: Playing with track id:', trackId)
      // Callback event
      track({
        event: TrackEventType.PLAY,
        custom: { content_id: trackId },
      })
      // User actions
      track({
        event: TrackEventType.PLAY_BUTTON_CLICKED,
      })
    },
    [track],
  )

  const onTracksLoaded = useCallback((tracks: EventTrack[]) => {
    debugLog(
      'cb: onTracksLoaded: Tracks successfully loaded into memory for rapid playback',
      tracks,
    )
  }, [])

  const onMuteToggle = useCallback(
    (isMuted: boolean) => {
      debugLog('cb: onMuteToggle: Mute toggled. New state is', { isMuted })
      // User actions
      if (isMuted) {
        track({
          event: TrackEventType.MUTE_BUTTON_CLICKED,
        })
      } else {
        track({
          event: TrackEventType.UNMUTE_BUTTON_CLICKED,
        })
      }
    },
    [track],
  )

  const onPlayNewTrack = useCallback(
    (oldTrackId: string, trackId: string) => {
      debugLog(
        'cb: onPlayNewTrack: Transitioning from one track to another',
        oldTrackId,
        trackId,
      )
      // Callback event
      track({
        event: TrackEventType.PLAY_NEW_TRACK,
        custom: { content_id: trackId },
      })
    },
    [track],
  )

  const handleError = useCallback(
    (trackId: string, circumstance: ErrorStates) => {
      // Callback event
      track({
        event: TrackEventType.PLAY_ERROR,
        custom: { content_id: trackId, circumstance },
      })
      debugError('cb: onPlayError: We encountered an error:', {
        circumstance,
        trackId,
      })
      navigate('/player/error')
    },
    [],
  )

  return (
    <SingleTrackAudioPlayer
      onTracksLoaded={onTracksLoaded}
      onPlay={onPlay}
      onActiveFinishedAtInit={onActiveFinishedAtInit}
      onActiveFinished={onActiveFinished}
      onMuteToggle={onMuteToggle}
      onPlayNewTrack={onPlayNewTrack}
      onError={handleError}
      onTracksLoadingError={e => {
        debugError(
          'cb: onTracksLoadingError: The tracks failed to load for some reason',
          e,
        )
        navigate('/player/error')
      }}
    />
  )
}
